import {
  createCustomerResponseSchema,
  walkInCustomerResponseSchema,
  type WalkInCustomerResponse,
  createCustomerPaymentMethodResponseSchema,
  getCustomerPaymentMethodResponseSchema,
  listCustomerAppointmentsResponseSchema,
  listCustomerTagsResponseSchema,
  createCustomerTagResponseSchema,
  deleteCustomerTagResponseSchema,
  bookingCustomerGateResponseSchema,
  customerAiSummaryResponseSchema,
  type CustomerAiSummaryResponse,
  type CreateCustomerRequest,
  type CreateCustomerResponse,
  type CreateCustomerPaymentMethodRequest,
  type CreateBookingPaymentMethodRequest,
  type CreateCustomerPaymentMethodResponse,
  type GetCustomerPaymentMethodResponse,
  type ListCustomerAppointmentsResponse,
  type CustomerPaymentMethod,
  type CustomerAppointmentItem,
  type ListCustomerTagsResponse,
  type CreateCustomerTagRequest,
  type CreateCustomerTagResponse,
  type DeleteCustomerTagResponse,
  type CustomerTag,
  type CustomerGate,
  type BookingCustomerGateRequest,
  type BookingCustomerGateResponse,
} from "@openings/api-contract";
import { BaseHttpClient, baseOk, ApiOk } from "./base";

const createCustomerResponse = baseOk(createCustomerResponseSchema);
const walkInCustomerResponse = baseOk(walkInCustomerResponseSchema);
const createPaymentMethodResponse = baseOk(
  createCustomerPaymentMethodResponseSchema,
);
const getPaymentMethodResponse = baseOk(getCustomerPaymentMethodResponseSchema);
const listAppointmentsResponse = baseOk(listCustomerAppointmentsResponseSchema);
const listTagsResponse = baseOk(listCustomerTagsResponseSchema);
const createTagResponse = baseOk(createCustomerTagResponseSchema);
const deleteTagResponse = baseOk(deleteCustomerTagResponseSchema);
const bookingGateResponse = baseOk(bookingCustomerGateResponseSchema);
const aiSummaryResponse = baseOk(customerAiSummaryResponseSchema);

export type CreateCustomerInput = CreateCustomerRequest;
export type CreateCustomerResult = ApiOk<CreateCustomerResponse>;
export type WalkInCustomerResult = ApiOk<WalkInCustomerResponse>;

/** Input for the public booking customer creation endpoint (rate-limited). */
export interface CreateBookingCustomerInput {
  salonId: string;
  userId: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  email?: string;
}
export type CreateBookingCustomerResult = ApiOk<CreateCustomerResponse>;
export type CreatePaymentMethodInput = CreateCustomerPaymentMethodRequest;
export type CreatePaymentMethodResult =
  ApiOk<CreateCustomerPaymentMethodResponse>;
export interface GetPaymentMethodInput {
  customerId: string;
}
export type PaymentMethod = CustomerPaymentMethod;
export type GetPaymentMethodResult = ApiOk<GetCustomerPaymentMethodResponse>;
export type CustomerAppointmentSummary = CustomerAppointmentItem;
export type ListCustomerAppointmentsResult =
  ApiOk<ListCustomerAppointmentsResponse>;

// Customer tag types
export type { CustomerTag, CustomerGate };
export type ListTagsInput = { customerId: string };
export type ListTagsResult = ApiOk<ListCustomerTagsResponse>;
export type CreateTagInput = { customerId: string } & CreateCustomerTagRequest;
export type CreateTagResult = ApiOk<CreateCustomerTagResponse>;
export type DeleteTagInput = { customerId: string; tagId: string };
export type DeleteTagResult = ApiOk<DeleteCustomerTagResponse>;

// AI summary types
export type { CustomerAiSummaryResponse };
export type GetAiSummaryInput = { salonId: string; customerId: string };
export type GetAiSummaryResult = ApiOk<CustomerAiSummaryResponse>;

// Public booking gate types
export type { BookingCustomerGateRequest, BookingCustomerGateResponse };
export type GetBookingGateInput = BookingCustomerGateRequest;
export type GetBookingGateResult = ApiOk<BookingCustomerGateResponse>;

export class CustomersClient extends BaseHttpClient {
  create(input: CreateCustomerInput): Promise<CreateCustomerResult> {
    return this.post<CreateCustomerResult>(
      "/v1/customers",
      input,
      createCustomerResponse,
    );
  }
  /** The business's walk-in customer, created on first use. */
  walkIn(input: { salonId: string }): Promise<WalkInCustomerResult> {
    return this.post<WalkInCustomerResult>(
      "/v1/customers/walk-in",
      input,
      walkInCustomerResponse,
    );
  }
  /**
   * Create (or find) a customer during the public booking flow.
   * Requires a valid OTP verification code instead of auth.
   */
  createForBooking(
    input: CreateBookingCustomerInput,
  ): Promise<CreateBookingCustomerResult> {
    return this.post<CreateBookingCustomerResult>(
      "/v1/customers/booking",
      input,
      createCustomerResponse,
    );
  }
  createPaymentMethod(
    input: CreatePaymentMethodInput,
  ): Promise<CreatePaymentMethodResult> {
    return this.post<CreatePaymentMethodResult>(
      "/v1/customers/payment-method/create",
      input,
      createPaymentMethodResponse,
    );
  }
  /**
   * Attach a payment method during the public booking flow (no auth required).
   * Rate-limited to prevent abuse.
   */
  createPaymentMethodForBooking(
    input: CreateBookingPaymentMethodRequest,
  ): Promise<CreatePaymentMethodResult> {
    return this.post<CreatePaymentMethodResult>(
      "/v1/customers/payment-method/booking",
      input,
      createPaymentMethodResponse,
    );
  }

  /**
   * @deprecated Use `createPaymentMethod` and persist references in caller workflows instead of fetching payment method details from this endpoint.
   */
  getPaymentMethod(
    input: GetPaymentMethodInput,
  ): Promise<GetPaymentMethodResult> {
    const qs = new URLSearchParams({ customerId: input.customerId });
    return this.get<GetPaymentMethodResult>(
      `/v1/customers/payment-method?${qs.toString()}`,
      getPaymentMethodResponse,
    );
  }
  listAppointments(input: {
    customerId: string;
    salonId: string;
  }): Promise<ListCustomerAppointmentsResult> {
    const qs = new URLSearchParams({ salonId: input.salonId });
    return this.get<ListCustomerAppointmentsResult>(
      `/v1/customers/${encodeURIComponent(
        input.customerId,
      )}/appointments?${qs.toString()}`,
      listAppointmentsResponse,
    );
  }

  /**
   * Public (no auth) endpoint for the customer manage page.
   * The customerId in the URL acts as the access token (sent via SMS link).
   */
  listManageAppointments(input: {
    customerId: string;
    salonId: string;
  }): Promise<ListCustomerAppointmentsResult> {
    const qs = new URLSearchParams({ salonId: input.salonId });
    return this.get<ListCustomerAppointmentsResult>(
      `/v1/customers/${encodeURIComponent(
        input.customerId,
      )}/manage/appointments?${qs.toString()}`,
      listAppointmentsResponse,
    );
  }

  // ── AI summary ────────────────────────────────────────────────────────────

  /**
   * Owner/manager AI summary of one client's visit behavior (cadence +
   * average spend). `summary` is null when the AI provider is unavailable;
   * metrics are always present.
   */
  getAiSummary(input: GetAiSummaryInput): Promise<GetAiSummaryResult> {
    return this.get<GetAiSummaryResult>(
      `/v1/salons/${encodeURIComponent(input.salonId)}/customers/${encodeURIComponent(
        input.customerId,
      )}/ai-summary`,
      aiSummaryResponse,
    );
  }

  // ── Tags ──────────────────────────────────────────────────────────────────

  /** List all tags for a customer (business + staff scopes). */
  listTags(input: ListTagsInput): Promise<ListTagsResult> {
    return this.get<ListTagsResult>(
      `/v1/customers/${encodeURIComponent(input.customerId)}/tags`,
      listTagsResponse,
    );
  }

  /** Create (or upsert) a customer tag. */
  createTag(input: CreateTagInput): Promise<CreateTagResult> {
    const { customerId, ...body } = input;
    return this.post<CreateTagResult>(
      `/v1/customers/${encodeURIComponent(customerId)}/tags`,
      body,
      createTagResponse,
    );
  }

  /** Delete a customer tag. */
  deleteTag(input: DeleteTagInput): Promise<DeleteTagResult> {
    return this.del<DeleteTagResult>(
      `/v1/customers/${encodeURIComponent(input.customerId)}/tags/${encodeURIComponent(input.tagId)}`,
      deleteTagResponse,
    );
  }

  // ── Public booking gate ──────────────────────────────────────────────────

  /**
   * Pre-check the customer gate before rendering the booking UI.
   * Public endpoint — no auth required, rate-limited.
   */
  getBookingGate(input: GetBookingGateInput): Promise<GetBookingGateResult> {
    return this.post<GetBookingGateResult>(
      "/v1/booking/customer-gate",
      input,
      bookingGateResponse,
    );
  }
}
